<script setup>
import PurchaseItemsTable from '@/components/purchase/PurchaseItemsTable.vue'
import { useApiRequest } from '@/composables/useApiRequest'
import { useApi } from '@/utils/api'

definePage({
  meta: {
    layout: 'default',
    action: 'update',
    subject: 'Purchase',
  },
})

const route  = useRoute()
const router = useRouter()
const { loading, errors, request } = useApiRequest()

// ─── State ────────────────────────────────────────────────────────────────────
const form = ref({
  supplier_id:  null,
  expected_at:  '',
  notes:        '',
})

// Shape generik dipakai PurchaseItemsTable — lihat catatan di komponennya.
const items       = ref([])
const fetchLoading = ref(true)
const fetchError   = ref('')

const suppliers   = ref([])
const ingredients = ref([])
const units       = ref([])

// ─── Load options + existing PO ──────────────────────────────────────────────
onMounted(async () => {
  try {
    const [sRes, iRes, uRes, poRes] = await Promise.all([
      useApi('/api/v1/suppliers', { query: { per_page: 200, is_active: 1 } }).get(),
      useApi('/api/v1/ingredients', { query: { per_page: 500 } }).get(),
      useApi('/api/v1/units', { query: { per_page: 200 } }).get(),
      useApi(`/api/v1/purchase-orders/${route.params.id}`).get(),
    ])

    const sBody  = sRes?.data  ?? sRes
    const iBody  = iRes?.data  ?? iRes
    const uBody  = uRes?.data  ?? uRes
    const poBody = poRes?.data ?? poRes
    const po     = poBody?.data ?? poBody

    // Guard: hanya draft boleh diedit
    if (!po?.is_editable) {
      fetchError.value = 'Hanya PO berstatus draft yang dapat diedit.'
      return
    }

    suppliers.value = (Array.isArray(sBody) ? sBody : (sBody?.data ?? [])).map(s => ({ title: s.name, value: s.id }))

    // unitId = satuan dasar ingredient ini sendiri — dipakai PurchaseItemsTable
    // utk filter pilihan "Satuan Pembelian" (satuan dasar + turunannya saja).
    ingredients.value = (Array.isArray(iBody) ? iBody : (iBody?.data ?? [])).map(i => ({
      title: i.name,
      value: i.id,
      unitId: i.unit?.id ?? null,
    }))

    // Satuan TIDAK di-map ke {title,value} — PurchaseItemsTable butuh field
    // aslinya (name, symbol, base_unit_id, conversion_factor) utk konversi.
    units.value = Array.isArray(uBody) ? uBody : (uBody?.data ?? [])

    form.value = {
      supplier_id: po.supplier?.id ?? null,
      expected_at: po.expected_at ? po.expected_at.substring(0, 10) : '',
      notes:       po.notes ?? '',
    }

    items.value = (po.items ?? []).map(i => ({
      ingredient_id: i.ingredient_id,
      unit_id:       i.unit_id,
      quantity:      i.quantity_ordered,
      total_price:   i.total_price ?? i.subtotal,
    }))

    if (items.value.length === 0) {
      items.value = [{ ingredient_id: null, unit_id: null, quantity: 1, total_price: 0 }]
    }
  } catch (err) {
    fetchError.value = 'Gagal memuat data purchase order.'
    console.error('[POEdit] mount error', err)
  } finally {
    fetchLoading.value = false
  }
})

// ─── Submit ───────────────────────────────────────────────────────────────────
const submit = async () => {
  const payload = {
    ...form.value,
    items: items.value.map(i => ({
      ingredient_id: i.ingredient_id,
      unit_id: i.unit_id,
      quantity_ordered: i.quantity,
      total_price: i.total_price,
    })),
  }

  const result = await request(() =>
    useApi(`/api/v1/purchase-orders/${route.params.id}`).put({ body: payload })
  )

  if (result !== null) {
    router.push({ name: 'purchase-orders-id', params: { id: route.params.id } })
  }
}
</script>

<template>
  <VRow>
    <VCol cols="12">
      <VCard>
        <VCardTitle class="pt-5 px-5">Edit Purchase Order</VCardTitle>

        <VDivider />

        <!-- Loading -->
        <VCardText v-if="fetchLoading" class="pa-10 text-center">
          <VProgressCircular indeterminate color="primary" />
        </VCardText>

        <!-- Error (not editable / fetch fail) -->
        <VCardText v-else-if="fetchError" class="pa-5">
          <VAlert type="warning" variant="tonal">{{ fetchError }}</VAlert>
          <VBtn
            class="mt-4"
            variant="tonal"
            :to="{ name: 'purchase-orders-id', params: { id: route.params.id } }"
          >
            Kembali ke Detail PO
          </VBtn>
        </VCardText>

        <!-- Form -->
        <VCardText v-else class="pa-5">
          <VAlert
            v-if="errors._general"
            type="error"
            variant="tonal"
            class="mb-4"
          >
            {{ errors._general[0] }}
          </VAlert>

          <VRow class="mb-2">
            <VCol cols="12" md="5">
              <VAutocomplete
                v-model="form.supplier_id"
                :items="suppliers"
                label="Supplier *"
                :error-messages="errors.supplier_id"
                no-data-text="Tidak ada supplier aktif"
              />
            </VCol>
            <VCol cols="12" md="3">
              <VTextField
                v-model="form.expected_at"
                label="Estimasi Tiba"
                type="date"
                :error-messages="errors.expected_at"
              />
            </VCol>
            <VCol cols="12" md="4">
              <VTextarea
                v-model="form.notes"
                label="Catatan"
                rows="2"
                auto-grow
              />
            </VCol>
          </VRow>

          <div class="text-subtitle-2 font-weight-semibold mb-3 mt-1">Item Pesanan</div>

          <PurchaseItemsTable
            v-model="items"
            :ingredients="ingredients"
            :units="units"
            :errors="errors"
            quantity-label="Qty"
          />
        </VCardText>

        <template v-if="!fetchLoading && !fetchError">
          <VDivider />
          <VCardActions class="pa-5">
            <VSpacer />
            <VBtn
              variant="tonal"
              :to="{ name: 'purchase-orders-id', params: { id: route.params.id } }"
            >
              Batal
            </VBtn>
            <VBtn color="primary" :loading="loading" @click="submit">
              Simpan Perubahan
            </VBtn>
          </VCardActions>
        </template>
      </VCard>
    </VCol>
  </VRow>
</template>